package fr.arcplex.network.cache

interface CacheStorage {
    suspend fun read(key: String): String?
    suspend fun write(key: String, value: String)
    suspend fun delete(key: String)
    suspend fun listKeys(): List<String>
}

/**
 * Whether the platform may wipe the storage at any time ([CACHE], e.g. Android's `cacheDir`)
 * or whether it must keep it until the app deletes it ([DURABLE], e.g. Android's `filesDir`).
 */
enum class StorageKind { CACHE, DURABLE }

/**
 * The [namespace] isolates one subsystem from another: `ResponseCache` sweeps every key it
 * finds in the storage it owns and deletes whatever it cannot parse, so a subsystem with
 * different entry shapes must never share a namespace with it.
 */
expect fun createCacheStorage(namespace: String = "api-cache", kind: StorageKind = StorageKind.CACHE): CacheStorage

expect fun currentTimeMillis(): Long

class MemoryCacheStorage : CacheStorage {
    private val entries = LinkedHashMap<String, String>()
    private val maxEntries = 512

    override suspend fun read(key: String): String? = entries[key]

    override suspend fun write(key: String, value: String) {
        entries[key] = value
        while (entries.size > maxEntries) {
            entries.remove(entries.keys.first())
        }
    }

    override suspend fun delete(key: String) {
        entries.remove(key)
    }

    override suspend fun listKeys(): List<String> = entries.keys.toList()
}
